import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * DateRangePicker - Atomic component for selecting date ranges
 */
const DateRangePicker = ({ 
  dateRange, 
  onDateRangeChange, 
  title = 'Analytics',
  presets = true
}) => {
  const [calendarOpen, setCalendarOpen] = React.useState(false);

  const handlePresetClick = (days) => {
    onDateRangeChange({
      from: subDays(new Date(), days - 1),
      to: new Date()
    });
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-xl font-bold text-[#1e3a8a]">{title}</h2>
          <div className="flex flex-wrap items-center gap-2">
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-full sm:w-auto justify-start text-left font-normal"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {dateRange.from && dateRange.to ? (
                    <>
                      {format(dateRange.from, 'MMM d, yyyy')} - {format(dateRange.to, 'MMM d, yyyy')}
                    </>
                  ) : (
                    <span>Pick a date range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <CalendarComponent
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => {
                    if (range?.from && range?.to) {
                      onDateRangeChange(range);
                      setCalendarOpen(false);
                    } else if (range?.from) {
                      onDateRangeChange({ from: range.from, to: range.from });
                    }
                  }}
                  numberOfMonths={2}
                  disabled={(date) => date > new Date()}
                />
              </PopoverContent>
            </Popover>
            
            {presets && (
              <>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handlePresetClick(30)}
                  className="hidden sm:inline-flex"
                >
                  Last 30 Days
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handlePresetClick(7)}
                  className="hidden sm:inline-flex"
                >
                  Last 7 Days
                </Button>
              </>
            )}
          </div>
        </div>
        
        {/* Mobile preset buttons */}
        {presets && (
          <div className="flex gap-2 mt-4 sm:hidden">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handlePresetClick(30)}
              className="flex-1"
            >
              Last 30 Days
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handlePresetClick(7)}
              className="flex-1"
            >
              Last 7 Days
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

DateRangePicker.propTypes = {
  dateRange: PropTypes.shape({
    from: PropTypes.instanceOf(Date),
    to: PropTypes.instanceOf(Date)
  }).isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  title: PropTypes.string,
  presets: PropTypes.bool
};

DateRangePicker.defaultProps = {
  title: 'Analytics',
  presets: true
};

export default DateRangePicker;
